import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  MenuItem,
  Grid,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';

/**
 * SearchResultsPage displays global search results.
 * Shows transcript snippets with matches highlighted.
 */
function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [domain, setDomain] = useState('');
  const resultsPerPage = 20;

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query, page, domain]);

  const performSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.globalSearch(query, {
        domain: domain || null,
        limit: resultsPerPage,
        offset: (page - 1) * resultsPerPage,
      });

      setResults(response.results);
      setTotal(response.total);
    } catch (err) {
      setError('Failed to perform search. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result) => {
    navigate(`/projects/${result.project_id}`);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleDomainChange = (event) => {
    setDomain(event.target.value);
    setPage(1); // Reset to first page
  };

  const totalPages = Math.ceil(total / resultsPerPage);

  if (!query) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h5">Enter a search query to find transcripts</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Search Results
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {loading ? 'Searching...' : `Found ${total} results for "${query}"`}
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Domain"
              value={domain}
              onChange={handleDomainChange}
              size="small"
            >
              <MenuItem value="">All Domains</MenuItem>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="medical">Medical</MenuItem>
              <MenuItem value="legal">Legal</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="history">History</MenuItem>
              <MenuItem value="business">Business</MenuItem>
              <MenuItem value="academic">Academic</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!loading && results.length === 0 && (
        <Alert severity="info">
          No results found for "{query}". Try a different search term.
        </Alert>
      )}

      {!loading && results.length > 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            {results.map((result) => (
              <Card key={result.transcript_id} sx={{ mb: 2 }}>
                <CardActionArea onClick={() => handleResultClick(result)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {result.project_name}
                      </Typography>
                      {result.domain && (
                        <Chip label={result.domain} size="small" color="primary" />
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {result.audio_filename}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        '& mark': {
                          backgroundColor: 'yellow',
                          fontWeight: 'bold',
                          padding: '0 2px',
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: result.snippet }}
                    />

                    {result.created_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {new Date(result.created_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
}

export default SearchResultsPage;
